import dayjs from 'dayjs';
import ReactToPrint from 'react-to-print';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { Button, Drawer, Tooltip } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState } from 'react';

//APP
import { buscaCidade } from '../../../../../../../../../../services/Afins';
import { useFormOsContext } from '../../context/FormOsContext';

//COMPONENTS
import Print from '../../../../../../../../../../Components/ordemLaboratorio/printOS';
import ButtonCustom from '../../../../../../../../../../Components/ButtonCustom';

/** Dados montados para o componente <Print/> (layout da folha). */
interface DadosImpressao {
    dataPrint: any
    dataFinish: any
}

/**
 * Botão "Imprimir ordem de coleta" + Drawer de impressão.
 * Reusa o componente compartilhado <Print/> para o layout da folha; aqui só
 * montamos os dados a partir do context e disparamos o react-to-print.
 */
export function ImpressaoOs() {
    const {
        campos,
        cod_os,
        isEdicao,
        isDirty,
        propriedade_atual,
        data_cliente,
        data_representantes,
        analises: { analises, malhaShp, talhaoArea, pontosTotalMalha, analisesDirty },
    } = useFormOsContext()

    const [abrir, setAbrir] = useState(false)
    const [loading, setLoading] = useState(false)
    const [dados, setDados] = useState<DadosImpressao | null>(null)
    const printRef = useRef<HTMLDivElement>(null)

    const semAnalises = (analises?.length ?? 0) <= 0
    /**
     * Com alterações pendentes a folha sairia com dados que não estão salvos —
     * a impressão representa a OS gravada, não o rascunho na tela.
     */
    const possuiAlteracoes = isDirty || analisesDirty
    const desabilitado = semAnalises || possuiAlteracoes

    const motivoDesabilitado = semAnalises
        ? 'Adicione ao menos uma análise para imprimir'
        : possuiAlteracoes
          ? 'Há alterações não salvas. Salve as alterações (ou feche e abra a O.S novamente) para imprimir.'
          : undefined

    // A impressão só faz sentido com a OS já salva (modo editar).
    if (!isEdicao) return null

    async function montarEAbrir() {
        setLoading(true)
        try {
            // Dados das análises para o <Print/>
            const dataFinish = {
                cod_laboratorio: campos.laboratorio_id,
                cod_propriedade: campos.propriedade_id,
                cod_talhao: campos.talhao_id,
                responsaveis: campos.responsaveis_selecionados?.map((r) => r?.descr),
                analises: analises?.map((item) => ({
                    cod_analises: [{ cod: item?.cod_analise, nome: item?.analise_nome, valor: item?.valor }],
                    profundidade: item?.profundidade,
                    pontos: item?.pontos_selecionados?.map((p) => ({
                        NOME: p?.NOME,
                        INDICE: p?.INDICE,
                        COMENTARIO: '',
                    })),
                })),
                malha_de_pontos: malhaShp,
            }

            // Cidade/UF a partir da propriedade selecionada
            const codCidade = (propriedade_atual as any)?.cod_cidade
            let cityUf = ''
            if (codCidade) {
                try {
                    const resp = await buscaCidade('', '', codCidade)
                    const { nome, uf_sigla } = resp[0]
                    cityUf = `${nome}/${uf_sigla}`
                } catch (e) {
                    /* cidade opcional */
                }
            }

            const representante = data_representantes?.find(
                (r) => r?.solicitante === campos.representante_selecionado
            )
            const dataPrint = {
                codOs: cod_os ?? 0,
                propriedadeNome: campos.propriedade,
                talhaoNome: campos.talhao,
                cityUf,
                representante: representante ?? {},
                outrosSolicitantes: campos.outros_solicitantes,
                cpf: data_cliente?.tipo === 'G' ? (representante as any)?.cpf : data_cliente?.cpf_cnpj,
                nome_cliente: data_cliente?.nome,
                // `data_coleta` só é preenchida quando a OS chega ao status COLETADA. Antes
                // disso a folha fica sem data mesmo — `''` em vez de `null` para o texto
                // "null" não aparecer impresso ao lado do rótulo.
                dataColeta: campos.coletada_em ? dayjs(campos.coletada_em).format('DD/MM/YYYY') : '',
                dataLiberada: campos.coleta_liberada ? dayjs(campos.coleta_liberada).format('DD/MM/YYYY') : '',
                areaAmostra: campos.area_amostra_laudo?.replace(' ha', ''),
            }

            setDados({ dataPrint, dataFinish })
            setAbrir(true)
        } finally {
            setLoading(false)
        }
    }

    function fechar() {
        setAbrir(false)
        setDados(null)
    }

    return (
        <>
            {/* O <span> é necessário: um botão desabilitado não emite os eventos
                de mouse que o Tooltip escuta. */}
            <Tooltip title={motivoDesabilitado}>
                <span>
                    <ButtonCustom
                        icon={<FontAwesomeIcon icon={faPrint} />}
                        type='secondary'
                        onClick={montarEAbrir}
                        loading={loading}
                        disabled={desabilitado}
                    >
                        Imprimir ordem de coleta
                    </ButtonCustom>
                </span>
            </Tooltip>

            <Drawer
                destroyOnClose
                width='820px'
                open={abrir}
                closeIcon={false}
                onClose={fechar}
                footer={
                    <ReactToPrint
                        trigger={() => (
                            <Button type='primary' title='Imprimir' icon={<PrinterOutlined />}>
                                Imprimir
                            </Button>
                        )}
                        content={() => printRef.current}
                        onBeforePrint={() => {
                            // Só no nome do arquivo a data ausente ganha rótulo: na folha ela
                            // fica vazia, mas aqui um segmento vazio deixaria o nome começando
                            // com "_".
                            const data_coleta = dados?.dataPrint?.dataColeta || 'não_coletado'
                            document.title = `${data_coleta}_${dados?.dataPrint?.nome_cliente}_${dados?.dataPrint?.talhaoNome}_${dados?.dataPrint?.propriedadeNome}`
                        }}
                        onAfterPrint={() => {
                            document.title = 'ttivos - gestão rural'
                        }}
                    />
                }
            >
                <div ref={printRef}>
                    {dados && (
                        <Print
                            dados={dados.dataFinish}
                            data={dados.dataPrint}
                            propriedade={(propriedade_atual as any) ?? {}}
                            areaTalhao={Number(talhaoArea) || 0}
                            qtdaPontos={pontosTotalMalha ?? 0}
                        />
                    )}
                </div>
            </Drawer>
        </>
    )
}
